import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useRef, useState } from 'react';

export interface QueryBuilderFilter {
  id: number;
  field: string;
  values: string[];
}

export interface QueryBuilderFieldOption {
  label: string;
  value: string;
}

interface QueryBuilderPanelProps {
  filters: QueryBuilderFilter[];
  onFiltersChange: (filters: QueryBuilderFilter[]) => void;
  filterFields?: QueryBuilderFieldOption[] | string[];
  filterValueOptions?: Record<string, string[]>;
  fetchFieldValues?: (field: string, searchTerm?: string) => Promise<string[]>;
  title?: string;
  runQueryButtonLabel?: string;
  onRunQuery?: (filters: QueryBuilderFilter[]) => void;
  containerSx?: SxProps<Theme>;
  paperSx?: SxProps<Theme>;
}

const dropdownPaperSx = {
  backgroundColor: '#ffffff !important',
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(15,23,42,0.12)',
  border: '1px solid #e2e8f0',
  mt: 0.5,
};

const QueryBuilderPanel = ({
  filters,
  onFiltersChange,
  filterFields = [],
  filterValueOptions = {},
  fetchFieldValues,
  title = 'Advance Filters',
  runQueryButtonLabel = 'RUN QUERY',
  onRunQuery,
  containerSx,
  paperSx,
}: QueryBuilderPanelProps) => {
  const nextId = useRef(filters.length);
  const generateId = () => ++nextId.current;

  // State to manage dynamically fetched options for each field
  const [dynamicFieldOptions, setDynamicFieldOptions] = useState<Record<string, string[]>>({});

  // Convert filterFields to normalized format (array of objects with label and value)
  const normalizedFieldOptions: QueryBuilderFieldOption[] = filterFields.map((field) => {
    if (typeof field === 'string') {
      return { label: field, value: field };
    }
    return field;
  });

  // Get available fields for a specific row (excluding already-used fields)
  const getAvailableFieldsForRow = (currentFilterId: number) => {
    const usedFields = new Set(
      filters
        .filter((f) => f.id !== currentFilterId && f.field)
        .map((f) => f.field)
    );
    return normalizedFieldOptions.filter((opt) => !usedFields.has(opt.value));
  };

  // Merge prop options with dynamically fetched options, prioritizing dynamic ones
  const getFieldOptions = (field: string) => {
    return dynamicFieldOptions[field] ?? filterValueOptions[field] ?? [];
  };

  const handleRemoveClause = (id: number) => {
    onFiltersChange(filters.filter((filter) => filter.id !== id));
  };

  const handleClearAll = () => {
    onFiltersChange([{ id: generateId(), field: normalizedFieldOptions[0]?.value ?? '', values: [] }]);
  };

  const handleChangeFilterField = (id: number, field: string) => {
    onFiltersChange(
      filters.map((filter) => (filter.id === id ? { ...filter, field, values: [] } : filter))
    );
  };

  const handleChangeFilterValues = (id: number, values: string[]) => {
    onFiltersChange(filters.map((filter) => (filter.id === id ? { ...filter, values } : filter)));
  };

  const hasInvalidFilters = filters.some(
    (filter) => filter.values.length === 0
  );

  const handleAddClause = () => {
    const usedFields = new Set(filters.map(f => f.field));
    const nextField = normalizedFieldOptions.find(
      option => !usedFields.has(option.value)
    )?.value ?? '';

    onFiltersChange([
      ...filters,
      {
        id: generateId(),
        field: nextField,
        values: [],
      },
    ]);
  };

  const handleRunQuery = () => {
    if (hasInvalidFilters) {
      return;
    }
    onRunQuery?.(filters);
  };
  const isOnlyFilter = filters.length === 1;
  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        overflow: 'visible',
        mb: 3,
        ...containerSx,
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.25,
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          borderRadius: '8px 8px 0 0',
          ...paperSx,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 700,
            color: '#1e293b',
            letterSpacing: '0.01em',
            mr: 2,
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Column labels & CLEAR ALL */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          pt: 1,
          pb: 0.25,
          gap: 1,
        }}
      >
        <Box sx={{ width: 100, flexShrink: 0 }}>
          <Button
            size="small"
            onClick={handleClearAll}
            startIcon={<CloseIcon sx={{ fontSize: '0.75rem !important' }} />}
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#94a3b8',
              textTransform: 'uppercase',
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              p: '2px 0',
              lineHeight: 1.4,
              '&:hover': { color: '#64748b' },
            }}
          >
            CLEAR ALL
          </Button>
        </Box>
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 700,
            color: '#1e293b',
            letterSpacing: '0.06em',
            flex: '0 0 calc(45% - 60px)',
          }}
        >
          Field
        </Typography>
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 700,
            color: '#1e293b',
            letterSpacing: '0.06em',
            flex: 1,
          }}
        >
          Value
        </Typography>
      </Box>

      {/* Filter rows */}
      <Box sx={{ 
        px: 2, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75, 
        maxHeight: 200,    
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#90A4AE',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#F5F5F5',
        },
        }}>
        {filters.map((filter) => (
          <Box
            key={filter.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, width: 100 }}>
              <IconButton
                onClick={() => handleRemoveClause(filter.id)}
                size="small"
                disabled={isOnlyFilter}
                sx={{
                  width: 26,
                  height: 26,
                  p: 0,
                  border: '1.5px solid',
                  borderColor: isOnlyFilter ? '#fda4af' : '#ef4444',
                  borderRadius: '6px',
                  backgroundColor: isOnlyFilter ? '#fff5f5' : '#fff',
                  color: isOnlyFilter ? '#fda4af' : '#ef4444',
                  '&:hover': {
                    backgroundColor: '#fef2f2',
                    borderColor: '#dc2626',
                    color: '#dc2626',
                  },
                  '&.Mui-disabled': {
                    borderColor: '#fda4af',
                    color: '#fda4af',
                    opacity: 1,
                    '& .MuiSvgIcon-root': {
                      color: '#fda4af',
                      opacity: 1,
                    },
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: '0.8rem', color: 'inherit' }} />
              </IconButton>

              <IconButton
                onClick={handleAddClause}
                size="small"
                sx={{
                  width: 26,
                  height: 26,
                  p: 0,
                  border: '1.5px solid #39ACE7',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                  color: '#39ACE7',
                  '&:hover': {
                    backgroundColor: '#f0f9ff',
                    borderColor: '#2D8BC0',
                    color: '#2D8BC0',
                  },
                }}
              >
                <AddIcon sx={{ fontSize: '0.8rem' }} />
              </IconButton>
            </Box>

            <Box sx={{ flex: '0 0 calc(45% - 60px)' }}>
              <TextField
                select
                value={filter.field}
                onChange={(e) => handleChangeFilterField(filter.id, e.target.value)}
                size="small"
                fullWidth
                SelectProps={{
                  MenuProps: {
                    disablePortal: false,
                    PaperProps: {
                      sx: {
                        ...dropdownPaperSx,
                        maxHeight: 280,       
                        overflowY: 'auto',
                        '& .MuiList-root': { backgroundColor: '#ffffff !important', py: 0.5 },
                        '& .MuiMenuItem-root': {
                          fontSize: '0.8125rem',
                          color: '#1e293b !important',
                          backgroundColor: '#ffffff !important',
                          py: 0.75,
                          '&:hover': { backgroundColor: '#f0f9ff !important' },
                          '&.Mui-selected': {
                            backgroundColor: '#e0f2fe !important',
                            color: '#1e293b !important',
                            '&:hover': { backgroundColor: '#bae6fd !important' },
                          },
                        },
                        '&::-webkit-scrollbar': {
                          width: 6,
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#90A4AE',
                          borderRadius: 3,
                        },
                      },
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f8fafc',
                    fontSize: '0.8125rem',
                    color: '#1e293b',
                    height: '34px',
                    '& fieldset': { borderColor: '#e0f2fe' },
                    '&:hover fieldset': { borderColor: '#bae6fd' },
                    '&.Mui-focused fieldset': { borderColor: '#39ACE7', borderWidth: '1.5px' },
                    '& .MuiSvgIcon-root': { color: '#39ACE7 !important' },
                  },
                  '& .MuiSelect-select': {
                    color: '#1e293b',
                    fontSize: '0.8125rem',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {getAvailableFieldsForRow(filter.id).map((option) => (
                  <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.8125rem', color: '#1e293b' }}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Autocomplete
                multiple
                freeSolo
                options={getFieldOptions(filter.field)}
                value={filter.values}
                onChange={(_, value) => handleChangeFilterValues(filter.id, value)}
                onInputChange={async (_, value, reason) => {
                  if (reason === 'input' && fetchFieldValues && filter.field) {
                    try {
                      const options = await fetchFieldValues(filter.field, value);
                      // Update state with fetched options
                      setDynamicFieldOptions((prev) => ({
                        ...prev,
                        [filter.field]: options,
                      }));
                    } catch (error) {
                      console.error('Error fetching field values:', error);
                    }
                  }
                }}
                disablePortal={false}
                slotProps={{
                  paper: {
                    sx: {
                      ...dropdownPaperSx,
                      '& .MuiAutocomplete-listbox': { backgroundColor: '#ffffff !important', py: 0.5 },
                      '& .MuiAutocomplete-option': {
                        fontSize: '0.8125rem',
                        color: '#1e293b !important',
                        backgroundColor: '#ffffff !important',
                        py: 0.75,
                        '&:hover': { backgroundColor: '#f0f9ff !important' },
                        '&.Mui-focused': { backgroundColor: '#e0f2fe !important' },
                        '&[aria-selected="true"]': { backgroundColor: '#e0f2fe !important', color: '#1e293b !important' },
                        '&[aria-selected="true"].Mui-focused': { backgroundColor: '#bae6fd !important' },
                      },
                      '& .MuiAutocomplete-noOptions': { fontSize: '0.8125rem', color: '#64748b', backgroundColor: '#ffffff' },
                    },
                  },
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, tagIndex) => (
                    <Tooltip key={option} title={option} arrow>
                    <Chip
                      label={option}
                      {...getTagProps({ index: tagIndex })}
                      key={option}
                      size="small"
                      sx={{
                        backgroundColor: '#EFF6FF !important',
                        color: '#1e3a5f !important',
                        border: '1px solid #BFDBFE !important',
                        borderRadius: '4px',
                        height: '22px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '60px',},
                        '& .MuiChip-deleteIcon': { color: '#869ec5', fontSize: '0.85rem', '&:hover': { color: '#1d4ed8' } },
                      }}
                    />
                    </Tooltip>
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={filter.values.length === 0 ? 'Select values…' : ''}
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: '#f8fafc',
                        fontSize: '0.8125rem',
                        minHeight: '34px',
                        '& fieldset': { borderColor: '#e0f2fe' },
                        '&:hover fieldset': { borderColor: '#bae6fd' },
                        '&.Mui-focused fieldset': { borderColor: '#39ACE7', borderWidth: '1.5px' },
                        '& input': { color: '#1e293b !important', fontSize: '0.8125rem' },
                        '& input::placeholder': { fontSize: '0.8125rem', color: '#94a3b8', opacity: 1 },
                        '& .MuiAutocomplete-popupIndicator': { color: '#39ACE7 !important' },
                        '& .MuiAutocomplete-clearIndicator': { color: '#94a3b8 !important' },
                      },
                    }}
                  />
                )}
                sx={{ width: '100%' }}
              />
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          px: 2,
          pb: 1.5,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleRunQuery}
          disabled={hasInvalidFilters}
          sx={{
            borderColor: '#39ACE7',
            color: '#39ACE7',
            backgroundColor: '#ffffff',
            textTransform: 'none',
            borderRadius: '4px',
            boxShadow: 'none',
            px: 3,
            py: 0.6,
            fontSize: '0.8125rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
            minWidth: '90px',
            '&:hover': {
              borderColor: '#2D8BC0',
              backgroundColor: '#f0f9ff',
              boxShadow: 'none',
            },
            "&.Mui-disabled": {
              color: '#94A3B8 !important',
              borderColor: '#94A3B8 !important',
              opacity: '1 !important',
              WebkitTextFillColor: '#94A3B8 !important',
            },
          }}
        >
          {runQueryButtonLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default QueryBuilderPanel;
